import React, { useEffect, useRef } from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';
import { bpConfig, genHostname } from './data';

interface EnrollCardProps {
  mode: 'enterprise' | 'mssp';
  breakpoint: Breakpoint;
}

const EnrollCard: React.FC<EnrollCardProps> = ({ mode, breakpoint }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    grid.innerHTML = '';

    const cfg = bpConfig[breakpoint];
    const isMssp = mode === 'mssp';
    const total = isMssp ? cfg.msspDotCount : cfg.dotCount;
    const dotSize = isMssp ? cfg.msspDotSize : cfg.dotSize;
    const gap = isMssp ? cfg.msspDotGap : cfg.dotGap;
    grid.style.gap = gap;

    if (total === 0) return; // mobile: no dots

    const fragment = document.createDocumentFragment();
    const baseDelay = total > 600 ? 2 : 6;
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('div');
      const r = Math.random();
      const threshold1 = isMssp ? 0.10 : 0.12;
      const threshold2 = isMssp ? 0.03 : 0.04;
      const status = r > threshold1 ? 'online' : r > threshold2 ? 'pending' : 'offline';
      const statusLabel = status === 'online' ? 'Online' : status === 'pending' ? 'Pending' : 'Offline';

      dot.className = `deploy-dot ${status}`;
      dot.style.width = `${dotSize}px`;
      dot.style.height = `${dotSize}px`;
      dot.style.borderRadius = '2px';
      dot.style.opacity = '0';
      dot.style.position = 'relative';
      dot.style.cursor = 'pointer';
      dot.style.transition = 'transform 0.15s, opacity 0.15s';
      dot.style.animation = `dotAppear 0.3s ease ${i * baseDelay}ms forwards`;

      if (status === 'online') dot.style.background = '#00CED1';
      else if (status === 'pending') dot.style.background = '#F59E0B';
      else dot.style.background = 'rgba(255,255,255,0.12)';

      const tip = document.createElement('span');
      tip.className = 'dot-tip';
      tip.innerHTML = `<span class="tip-host" style="font-weight:500;color:#fff;">${genHostname(i, isMssp)}</span> · <span style="color:${status === 'online' ? '#00CED1' : status === 'pending' ? '#F59E0B' : 'rgba(255,255,255,0.4)'};">${statusLabel}</span>`;
      dot.appendChild(tip);

      fragment.appendChild(dot);
    }
    grid.appendChild(fragment);
  }, [mode, breakpoint]);

  return (
    <div className="glass-card">
      <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 01 — Enroll</div>
      <div className="text-xl font-light text-white/90 mb-4">Enroll Your Assets</div>
      <div
        ref={gridRef}
        className="flex flex-wrap mb-4 relative"
      />
      <div className="flex gap-5 mt-auto pt-3 border-t border-white/[0.04] font-mono text-[12px] text-white/70 tracking-[0.04em]">
        One read-only agent per endpoint. It never writes, modifies or deletes.
      </div>
    </div>
  );
};

export default EnrollCard;
